import has from 'lodash/has'
import isEqual from 'lodash/isEqual'
import last from 'lodash/last'

import { MessagingType } from 'apps/posting/constants/MessagingType'
import { IPost } from 'apps/posting/postingInterfaces'
import { IBuilderChannelsInfo } from 'common/builder/builderInterfaces'
import { FBMessagingType } from 'common/builder/constants/facebookMessaging'
import { ChannelType } from 'common/core/constants/ChannelType'
import { NotificationType } from 'common/core/constants/NotificationType'
import { FieldSegmentType } from 'common/fields/entities/enums'
import { FilterOperator } from 'common/filter/filterConstants'
import { FilterGroup } from 'common/filter/interfaces'
import { FilterField, FieldTypeOperator } from 'common/filter/models/AudienceFilter/constants'

const telegramSpecialGroup: FilterGroup = {
  operator: FilterOperator.AND,
  items: [
    {
      type: FieldSegmentType.SUF,
      field: FilterField.OPTIN_TELEGRAM,
      operator: FieldTypeOperator.TRUE,
    },
  ],
}

const emailSpecialGroup: FilterGroup = {
  operator: FilterOperator.AND,
  items: [
    {
      type: FieldSegmentType.SUF,
      field: FilterField.OPTIN_EMAIL,
      operator: FieldTypeOperator.TRUE,
    },
    { type: FieldSegmentType.SUF, field: FilterField.EMAIL, operator: FieldTypeOperator.HAS_VALUE },
  ],
}

const smsSpecialGroup: FilterGroup = {
  operator: FilterOperator.AND,
  items: [
    {
      type: FieldSegmentType.SUF,
      field: FilterField.OPTIN_PHONE,
      operator: FieldTypeOperator.TRUE,
    },
    { type: FieldSegmentType.SUF, field: FilterField.PHONE, operator: FieldTypeOperator.HAS_VALUE },
  ],
}

const messengerSpecialGroup: FilterGroup = {
  operator: FilterOperator.AND,
  items: [
    { type: FieldSegmentType.SUF, field: FilterField.MESSENGER, operator: FieldTypeOperator.TRUE },
  ],
}

const messengerWithRecurringNotifyGroup = (reasonId: number): FilterGroup => ({
  operator: FilterOperator.AND,
  items: [
    { type: FieldSegmentType.SUF, field: FilterField.MESSENGER, operator: FieldTypeOperator.TRUE },
    {
      type: FieldSegmentType.ONE_TIME_NOTIFICATION,
      field: FilterField.ONE_TIME_NOTIFICATION,
      operator: FieldTypeOperator.IS,
      value: reasonId,
    },
  ],
})

const messengerWith24HoursGroup = (): FilterGroup => ({
  operator: FilterOperator.AND,
  items: [
    { type: FieldSegmentType.SUF, field: FilterField.MESSENGER, operator: FieldTypeOperator.TRUE },
    {
      type: FieldSegmentType.SUF,
      field: FilterField.MESSAGING_WINDOW,
      operator: FieldTypeOperator.CASE,
      value: 'recent interaction (< 24 hrs)',
    },
  ],
})

const instagramWithRecurringNotifyGroup = (reasonId: number): FilterGroup => ({
  operator: FilterOperator.AND,
  items: [
    {
      type: FieldSegmentType.SUF,
      field: FilterField.OPTIN_INSTAGRAM,
      operator: FieldTypeOperator.TRUE,
    },
    {
      type: FilterField.IG_RECURRING_NOTIFICATION,
      field: FilterField.IG_RECURRING_NOTIFICATION,
      operator: FieldTypeOperator.IS,
      value: reasonId,
    },
  ],
})

const instagramWith24HoursGroup = (): FilterGroup => ({
  operator: FilterOperator.AND,
  items: [
    {
      type: FieldSegmentType.SUF,
      field: FilterField.OPTIN_INSTAGRAM,
      operator: FieldTypeOperator.TRUE,
    },
    {
      type: FieldSegmentType.SUF,
      field: FilterField.IG_MESSAGING_WINDOW,
      operator: FieldTypeOperator.CASE,
      value: 'recent interaction (< 24 hrs)',
    },
  ],
})

const whatsappSpecialGroup: FilterGroup = {
  operator: FilterOperator.AND,
  items: [
    {
      type: FieldSegmentType.SUF,
      field: FilterField.OPTIN_WHATSAPP,
      operator: FieldTypeOperator.TRUE,
    },
  ],
}

export const getPostSettingsChangesAdjustedForConfiguredChannels = (
  post: IPost,
  channelsInfo: IBuilderChannelsInfo,
  reasonId: number | null = null,
  messageType?: FBMessagingType,
  firstAiAgentNodeChannelBeforeChannelNode: ChannelType | null = null,
): Partial<IPost> => {
  const changes: Partial<IPost> = {}

  if (!channelsInfo.hasContent) {
    if (post.messageTag || post.messagingType !== MessagingType.NON_PROMO) {
      changes.messagingType = MessagingType.NON_PROMO
      changes.messageTag = null
    }
    if (post.notification !== NotificationType.REGULAR) {
      changes.notification = NotificationType.REGULAR
    }
  }

  if (channelsInfo.isMixed) {
    changes.messagingType = MessagingType.NON_PROMO
  }

  if (
    channelsInfo.hasContent &&
    (messageType || firstAiAgentNodeChannelBeforeChannelNode === ChannelType.FB)
  ) {
    switch (true) {
      case messageType === FBMessagingType.INSIDE_24_HOURS ||
        firstAiAgentNodeChannelBeforeChannelNode === ChannelType.FB:
        changes.messagingType = MessagingType.PROMO
        break
      case messageType === FBMessagingType.SUBSCRIPTION_CONTENT:
        changes.messagingType = MessagingType.SUBSCRIPTION
        break
      default:
        changes.messagingType = MessagingType.NON_PROMO
    }
  }

  // keep user entered filter group
  const lastFilterGroup = last(post.filter?.groups)

  const isLastFilterGroupSpecial =
    isSpecialEmailGroup(lastFilterGroup) ||
    isSpecialSMSGroup(lastFilterGroup) ||
    isSpecialMessengerGroup(lastFilterGroup) ||
    isSpecialTelegramGroup(lastFilterGroup) ||
    isSpecialWhatsAppGroup(lastFilterGroup) ||
    isSpecialInstagramGroup(lastFilterGroup)

  const isSingleChannel = !channelsInfo.isMixed && !channelsInfo.isUnknown

  if (
    isSingleChannel &&
    channelsInfo.hasContent &&
    !messageType &&
    !firstAiAgentNodeChannelBeforeChannelNode
  ) {
    changes.messagingType = MessagingType.NON_PROMO
  }

  // for single channel sms or email broadcast adding special filter group
  if (isSingleChannel) {
    changes.filter = {
      operator: FilterOperator.AND,
      groups: [],
    }
    if (channelsInfo.hasContent) {
      if (reasonId && !firstAiAgentNodeChannelBeforeChannelNode) {
        changes.filter.groups.push(messengerWithRecurringNotifyGroup(reasonId))
      } else if (
        messageType === FBMessagingType.INSIDE_24_HOURS ||
        firstAiAgentNodeChannelBeforeChannelNode === ChannelType.FB
      ) {
        changes.filter.groups.push(messengerWith24HoursGroup())
      } else {
        changes.filter.groups.push(messengerSpecialGroup)
      }
    } else if (channelsInfo.hasInstagram) {
      if (reasonId && !firstAiAgentNodeChannelBeforeChannelNode) {
        changes.filter.groups.push(instagramWithRecurringNotifyGroup(reasonId))
      } else {
        changes.filter.groups.push(instagramWith24HoursGroup())
      }
    } else if (channelsInfo.hasSMS) {
      changes.filter.groups.push(smsSpecialGroup)
    } else if (channelsInfo.hasEmail) {
      changes.filter.groups.push(emailSpecialGroup)
    } else if (channelsInfo.hasTelegram) {
      changes.filter.groups.push(telegramSpecialGroup)
    } else if (channelsInfo.hasWhatsApp) {
      changes.filter.groups.push(whatsappSpecialGroup)
    }

    if (lastFilterGroup && !isLastFilterGroupSpecial) {
      changes.filter.groups.push(lastFilterGroup)
    }
  }

  // for all other types removing this special group
  if (!isSingleChannel) {
    changes.filter = null
    if (lastFilterGroup && !isLastFilterGroupSpecial) {
      changes.filter = {
        operator: FilterOperator.AND,
        groups: [lastFilterGroup],
      }
    }
  }

  if (has(changes, 'filter') && isEqual(changes.filter, post.filter)) {
    delete changes.filter
  }

  if (has(changes, 'messagingType') && isEqual(changes.messagingType, post.messagingType)) {
    delete changes.messagingType
  }

  return changes
}

export const isSpecialEmailGroup = (filterGroup: FilterGroup | undefined) => {
  const firstItem = filterGroup?.items[0]
  const secondItem = filterGroup?.items[1]

  const hasOptInEmailFirstItem =
    firstItem?.field === FilterField.OPTIN_EMAIL && firstItem?.operator === FieldTypeOperator.TRUE
  const hasEmailValueSecondItem =
    secondItem?.field === FilterField.EMAIL && secondItem?.operator === FieldTypeOperator.HAS_VALUE
  return hasOptInEmailFirstItem && hasEmailValueSecondItem
}

export const isSpecialSMSGroup = (filterGroup?: FilterGroup) => {
  const firstItem = filterGroup?.items[0]
  const secondItem = filterGroup?.items[1]

  const hasOptInPhoneFirstItem =
    firstItem?.field === FilterField.OPTIN_PHONE && firstItem?.operator === FieldTypeOperator.TRUE
  const hasPhoneValueSecondItem =
    secondItem?.field === FilterField.PHONE && secondItem?.operator === FieldTypeOperator.HAS_VALUE
  return hasOptInPhoneFirstItem && hasPhoneValueSecondItem
}

export const isSpecialMessengerGroup = (filterGroup?: FilterGroup) => {
  return isSpecialGroupByField(filterGroup, FilterField.MESSENGER)
}

export const isSpecialTelegramGroup = (filterGroup?: FilterGroup) => {
  return isSpecialGroupByField(filterGroup, FilterField.OPTIN_TELEGRAM)
}

export const isSpecialWhatsAppGroup = (filterGroup?: FilterGroup) => {
  return isSpecialGroupByField(filterGroup, FilterField.OPTIN_WHATSAPP)
}

export const isSpecialInstagramGroup = (filterGroup?: FilterGroup) => {
  return isSpecialGroupByField(filterGroup, FilterField.OPTIN_INSTAGRAM)
}

const isSpecialGroupByField = (filterGroup: FilterGroup | undefined, field: FilterField) => {
  const firstItem = filterGroup?.items[0]
  return firstItem?.field === field && firstItem?.operator === FieldTypeOperator.TRUE
}
